/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { uploadChunked } from "../SubcontractOps";
import { fmtD } from "../../lib/date";
import { Camera, Loader2, Mail, MessageSquare, Package, Building2, Truck, Pencil, Trash2, Save, Ban } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Number(n || 0).toLocaleString();

export const DEFAULT_PICKUP_DAYS = 3;
export const emptyDelivery = () => ({ method: "pickup", pickupDays: DEFAULT_PICKUP_DAYS, envelopePhoto: null, notifySms: true, notifyEmail: true, note: "", noteTouched: false });

/* 支票领取方式（必选）：自取（N 个工作日后来公司领取，默认 3，可改）/ 邮寄（信封照片可稍后补传）+ 短信 / 邮件 勾选 + 英文附言（通用模板，可编辑 / 存为模板）—— 打印弹窗与已付行「设置领取」共用 */
export function DeliveryPicker({ r, zh, api, value, onChange, testid = "delivery" }) {
  const [uploading, setUploading] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [savingTpl, setSavingTpl] = useState(false);
  const c = r.payeeContact || {};
  const set = (patch) => onChange({ ...value, ...patch });
  const valueRef = useRef(value); valueRef.current = value;
  useEffect(() => {
    api.get("/finance/delivery-notice-template").then(({ data }) => { setTpl(data); if (!valueRef.current.noteTouched && !valueRef.current.note) onChange({ ...valueRef.current, note: data.text || "" }); }).catch(() => setTpl({ text: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  const saveTpl = async () => {
    setSavingTpl(true);
    try { const { data } = await api.put("/finance/delivery-notice-template", { text: value.note }); setTpl(data); toast.success(zh ? "已存为通用附言模板（各工种共用）" : "Saved as the shared template"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSavingTpl(false);
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try { const up = await uploadChunked(api, f); set({ envelopePhoto: up }); toast.success(zh ? "信封照片已上传" : "Envelope photo uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    setUploading(false);
    e.target.value = "";
  };
  const days = Number(value.pickupDays) || DEFAULT_PICKUP_DAYS;
  const opt = (k, Icon, label, hint, extra) => (
    <label key={k} data-testid={`${testid}-method-${k}`} className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 cursor-pointer text-xs ${value.method === k ? "border-brand-500 bg-brand-50" : "border-slate-200 hover:border-slate-300"}`}>
      <input type="radio" name={`${testid}-method`} checked={value.method === k} onChange={() => set({ method: k })} className="mt-0.5" />
      <Icon className="h-3.5 w-3.5 mt-0.5 text-slate-500 shrink-0" />
      <span className="flex-1 min-w-0"><b className="text-slate-800">{label}</b>{hint && <span className="block text-[11px] text-slate-500">{hint}</span>}{extra}</span>
    </label>
  );
  const dayInput = value.method === "pickup" && (
    <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-700" onClick={(e) => e.preventDefault()}>
      {zh ? "发出后" : "Ready in"}
      <input type="number" min="1" max="30" value={value.pickupDays ?? DEFAULT_PICKUP_DAYS} onChange={(e) => set({ pickupDays: Math.max(1, Math.min(30, Number(e.target.value) || DEFAULT_PICKUP_DAYS)) })} onClick={(e) => e.stopPropagation()} className="w-14 border border-slate-300 rounded-md px-1.5 py-0.5 text-center bg-white" data-testid={`${testid}-pickup-days`} />
      {zh ? "个工作日后可领取（默认 3）" : "business days (default 3)"}
    </span>
  );
  const tplDirty = tpl && (value.note || "") !== (tpl.text || "");
  return (
    <div className="space-y-2" data-testid={`${testid}-picker`} data-method={value.method || "none"} data-days={days}>
      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Package className="h-3.5 w-3.5" />{zh ? "支票交付 · 通知分包商（必选）" : "Check delivery · notify subcontractor (required)"}</p>
      <div className="grid gap-1.5">
        {opt("pickup", Building2, zh ? `自取 · ${days} 个工作日后来公司领取` : `Pickup · ready in ${days} business day${days === 1 ? "" : "s"}`, zh ? "系统按工作日自动算出可领取日期并写进通知" : "Pickup date is computed automatically", dayInput)}
        {opt("mail", Truck, zh ? "邮寄 · 信封照片可稍后补传" : "Mail · envelope photo can be added later", zh ? "建议拍邮寄信封留证；没拍也能确认，之后在已付行补传" : "Envelope photo recommended; can be uploaded later from the paid row")}
      </div>
      {value.method === "mail" && (
        <div className="flex items-center gap-2 text-xs" data-testid={`${testid}-envelope`} data-has={value.envelopePhoto ? 1 : 0}>
          <label className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border cursor-pointer ${value.envelopePhoto ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-slate-50 text-slate-700"}`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            {value.envelopePhoto ? (zh ? "信封照片已上传 · 重拍" : "Envelope uploaded · retake") : (zh ? "拍 / 上传邮寄信封照片（可选，可后补）" : "Upload envelope photo (optional)")}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-envelope-input`} />
          </label>
          {value.envelopePhoto && <span className="text-[11px] text-slate-500 truncate max-w-[180px]">{value.envelopePhoto.filename}</span>}
        </div>
      )}
      {value.method && (
        <div className="space-y-1.5 text-xs" data-testid={`${testid}-channels`}>
          <div className="flex flex-wrap gap-3">
            <label className={`inline-flex items-center gap-1.5 ${c.phone ? "text-slate-700" : "text-slate-400"}`}>
              <input type="checkbox" checked={!!value.notifySms && !!c.phone} disabled={!c.phone} onChange={(e) => set({ notifySms: e.target.checked })} data-testid={`${testid}-sms`} />
              <MessageSquare className="h-3.5 w-3.5" />{zh ? "短信" : "SMS"}{c.phone ? ` ${c.phoneMasked}` : (zh ? "（分包商无手机号）" : " (no phone)")}
            </label>
            <label className={`inline-flex items-center gap-1.5 ${c.email ? "text-slate-700" : "text-slate-400"}`}>
              <input type="checkbox" checked={!!value.notifyEmail && !!c.email} disabled={!c.email} onChange={(e) => set({ notifyEmail: e.target.checked })} data-testid={`${testid}-email`} />
              <Mail className="h-3.5 w-3.5" />{zh ? "邮件" : "Email"}{c.email ? ` ${c.emailAddr}` : (zh ? "（分包商无邮箱）" : " (no email)")}
            </label>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500"><span>{zh ? "英文附言（通用模板 · 各工种共用，可临时改动）" : "English note (shared template, editable)"}</span>
              {tplDirty && <button type="button" onClick={saveTpl} disabled={savingTpl} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-0.5 text-indigo-700 hover:underline" data-testid={`${testid}-note-save`}>{savingTpl ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}{zh ? "存为通用模板" : "Save as template"}</button>}
              {tpl?.updatedBy && !tplDirty && <span className="ml-auto" data-testid={`${testid}-note-tpl-info`}>{zh ? `模板 · ${tpl.updatedBy}` : `Template · ${tpl.updatedBy}`}</span>}
            </div>
            <textarea value={value.note} onChange={(e) => set({ note: e.target.value, noteTouched: true })} rows={2} placeholder={zh ? "英文附言（随短信 / 邮件一起发给分包商）" : "English note sent with the SMS / email"} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1 text-xs" data-testid={`${testid}-note`} />
          </div>
        </div>
      )}
    </div>
  );
}

export const deliveryBody = (v) => (v.method ? { delivery: { method: v.method, pickupDays: Number(v.pickupDays) || DEFAULT_PICKUP_DAYS, envelopePhoto: v.envelopePhoto || undefined, notifySms: !!v.notifySms, notifyEmail: !!v.notifyEmail, note: v.note } } : {});

/* 已付行上的交付状态芯片；邮寄未传信封 → 可直接补传（需 api + onDone） */
export function DeliveryChip({ r, zh, token, api, onDone }) {
  const [up, setUp] = useState(false);
  const d = r.delivery;
  if (!d) return null;
  const sent = d.sent || {};
  const ch = [sent.sms ? (sent.sms.ok ? (zh ? "短信✓" : "SMS✓") : (zh ? "短信×" : "SMS×")) : null, sent.email ? (sent.email.ok ? (zh ? "邮件✓" : "Email✓") : (zh ? "邮件×" : "Email×")) : null].filter(Boolean).join(" ");
  const addEnvelope = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUp(true);
    try { const photo = await uploadChunked(api, f); await api.post(`/payment-requests/${r.id}/delivery/envelope`, { envelopePhoto: photo }); toast.success(zh ? "信封照片已补传" : "Envelope photo added"); onDone?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    setUp(false); e.target.value = "";
  };
  return (
    <span data-testid={`pay-delivery-${r.id}`} data-method={d.method} data-envelope={d.envelopePhoto?.fileId ? 1 : 0} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${d.method === "mail" ? "bg-sky-100 text-sky-800" : "bg-emerald-100 text-emerald-800"}`} title={d.message?.en || d.message?.zh || ""}>
      {d.method === "mail" ? <Truck className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
      {d.method === "mail" ? (zh ? `已邮寄 ${fmtD(d.mailedAt)}` : `Mailed ${fmtD(d.mailedAt)}`) : (zh ? `自取 · ${fmtD(d.pickupDate)} 起` : `Pickup from ${fmtD(d.pickupDate)}`)}
      {ch && <span className="opacity-80">· {ch}</span>}
      {d.envelopePhoto?.fileId && <a href={`${BACKEND}/api/files/${d.envelopePhoto.fileId}/download?auth=${token}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="underline" data-testid={`pay-delivery-envelope-${r.id}`}>{zh ? "信封" : "envelope"}</a>}
      {d.method === "mail" && !d.envelopePhoto?.fileId && api && (
        <label onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-0.5 underline cursor-pointer text-amber-800" data-testid={`pay-delivery-envelope-add-${r.id}`}>
          {up ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}{zh ? "补传信封" : "add envelope"}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addEnvelope} data-testid={`pay-delivery-envelope-input-${r.id}`} />
        </label>
      )}
    </span>
  );
}

/* 已付后补设领取方式 / 改邮寄 */
export function DeliveryDialog({ r, zh, api, onClose, onDone }) {
  const [v, setV] = useState(emptyDelivery());
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!v.method) return toast.error(zh ? "请选择 自取 或 邮寄" : "Pick pickup or mail");
    setBusy(true);
    try {
      const { data } = await api.post(`/payment-requests/${r.id}/delivery`, deliveryBody(v));
      const s = data?.delivery?.sent || {};
      toast.success(zh ? `已记录${v.method === "mail" ? "邮寄" : "自取"}${s.sms?.ok || s.email?.ok ? " · 通知已发送" : ""}` : "Delivery saved");
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="delivery-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "支票领取 / 通知分包商" : "Check delivery / notify"}</DialogTitle>
          <DialogDescription>{r.tradeName} · {r.milestoneName} · {money(r.amount)}{r.checkNo ? ` · #${r.checkNo}` : ""}</DialogDescription>
        </DialogHeader>
        <DeliveryPicker r={r} zh={zh} api={api} value={v} onChange={setV} testid="delivery-dlg" />
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy} onClick={submit} data-testid="delivery-dlg-submit" className="bg-brand-500 hover:bg-brand-600 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "确认并通知" : "Confirm & notify")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* 管理员 / CEO：修正过往支付数据（付款日期 · 支票号 · 金额 · 备注 + 原因） */
export function AdminEditDialog({ r, zh, api, onClose, onDone }) {
  const [f, setF] = useState({ paidAt: (r.paidAt || "").slice(0, 10), checkNo: r.checkNo || "", amount: r.amount || "", note: r.note || "", reason: "" });
  const [busy, setBusy] = useState(false);
  const inp = "w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm";
  const submit = async () => {
    if (!f.reason.trim()) return toast.error(zh ? "请填写修正原因" : "Reason required");
    setBusy(true);
    try {
      await api.patch(`/payment-requests/${r.id}/admin-edit`, { paidAt: f.paidAt || undefined, checkNo: f.checkNo, amount: Number(f.amount) || undefined, note: f.note, reason: f.reason.trim() });
      toast.success(zh ? "已修正并留痕" : "Updated");
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4" />{zh ? "修正支付记录（管理员）" : "Edit payment record (admin)"}</DialogTitle>
          <DialogDescription>{r.tradeName} · {r.milestoneName} · {zh ? "每次修正都会写入审批历史与审计日志，并同步账单 / 支票 / 阶段" : "Every edit is logged and synced to bill / check / milestone"}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-600">
          <label>{zh ? "付款日期" : "Paid date"}<input type="date" value={f.paidAt} onChange={(e) => setF({ ...f, paidAt: e.target.value })} className={inp} data-testid="admin-edit-paidat" /></label>
          <label>{zh ? "支票号" : "Check #"}<input value={f.checkNo} onChange={(e) => setF({ ...f, checkNo: e.target.value })} className={inp} data-testid="admin-edit-checkno" /></label>
          <label>{zh ? "金额" : "Amount"}<input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inp} data-testid="admin-edit-amount" /></label>
          <label className="sm:col-span-2">{zh ? "备注" : "Note"}<input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inp} data-testid="admin-edit-note" /></label>
          <label className="sm:col-span-2">{zh ? "修正原因（必填）" : "Reason (required)"}<input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={inp} placeholder={zh ? "例：支票号登记错误，实际为 #4002" : "e.g. wrong check number"} data-testid="admin-edit-reason" /></label>
        </div>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy} onClick={submit} data-testid="admin-edit-submit" className="bg-brand-500 hover:bg-brand-600 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "保存修正" : "Save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* 财务 / 出纳 / 工程经理 / 管理员：作废已支付记录（记录保留 + 支票作废 + 账单退回未付 + 节点退回待发起 + Lien Waiver 作废）
   需求（用户 2026-10-22）：项目 1636 保温隔热第一张支票没有供应商公司名，只能另开一张，同一笔 $3,240
   变成两条「已支付」。原来只有管理员的「删除」（记录直接消失），财务和工程经理都无法自助纠正，
   也看不出「曾经付过又作废」，所以新增作废入口：状态标 void，全链路留痕，可重新发起付款。 */
export function PaymentVoidDialog({ r, zh, api, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notifySub, setNotifySub] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error(zh ? "请填写作废原因" : "Reason required");
    setBusy(true);
    try {
      const { data } = await api.post(`/payment-requests/${r.id}/void`, { reason: reason.trim(), notify: notifySub ? { sms: true, email: true } : undefined });
      const parts = [zh ? "付款记录已作废" : "Payment voided"];
      if (data?.voidedCheck) parts.push(zh ? "支票已作废" : "check voided");
      if (data?.billsReset) parts.push(zh ? "账单已退回未付" : "bill reopened");
      if (data?.voidedLien) parts.push(zh ? `Lien Waiver ${data.voidedLien.docNo} 已作废` : `lien ${data.voidedLien.docNo} voided`);
      parts.push(zh ? "该付款节点已退回「待发起」，可重新发起付款" : "milestone released");
      toast.success(parts.join(" · "));
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="payment-void-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="h-4 w-4" />{zh ? "作废已支付的付款记录" : "Void a paid record"}</DialogTitle>
          <DialogDescription>
            {r.tradeName} · {r.milestoneName} · <b className="text-red-600">{money(r.amount)}</b>{r.checkNo ? ` · ${zh ? "支票" : "check"} #${r.checkNo}` : ""}
            <br />{zh ? "记录会保留并标记「已作废」，不会消失。同时：作废关联支票、账单退回未付、该付款节点退回「待发起」（可重新付款）；若已自动生成 Lien Waiver 也一并作废。" : "The record is kept and marked void. The check is voided, the bill reopens, the milestone is released, and any auto-generated lien waiver is voided."}
          </DialogDescription>
        </DialogHeader>
        <label className="block text-xs text-slate-600" data-testid="payment-void-reason-label">
          {zh ? "作废原因（必填，写入审批历史与审计）" : "Reason (required, recorded)"}
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} data-testid="payment-void-reason"
            placeholder={zh ? "例：支票未印供应商公司名，已重开一张，此笔为重复记录" : "e.g. check issued without the vendor company name; reissued"} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="payment-void-notify-label">
          <input type="checkbox" checked={notifySub} onChange={(e) => setNotifySub(e.target.checked)} className="mt-0.5 rounded border-slate-300" />
          <span>{zh ? "同时通知分包商这笔付款已作废（短信 + 邮件，防止其继续兑付该支票）" : "Also notify the subcontractor (SMS + email) that this payment was voided"}</span>
        </label>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy || !reason.trim()} onClick={submit} data-testid="payment-void-submit" className="bg-red-600 hover:bg-red-700 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "确认作废" : "Void payment")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* 管理员 / CEO：删除支付记录（作废支票 · 删账单 · 阶段退回） */
export function AdminDeleteDialog({ r, zh, api, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error(zh ? "请填写删除原因" : "Reason required");
    setBusy(true);
    try {
      await api.delete(`/payment-requests/${r.id}`, { params: { reason: reason.trim() } });
      toast.success(zh ? "支付记录已删除：支票已作废、账单已删除、阶段退回「计划中」" : "Deleted");
      onDone();
    } catch (e) { toast.error(e?.detailText || e.message); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="h-4 w-4" />{zh ? "删除支付记录（管理员）" : "Delete payment record (admin)"}</DialogTitle>
          <DialogDescription>{r.tradeName} · {r.milestoneName} · <b className="text-red-600">{money(r.amount)}</b>{r.checkNo ? ` · #${r.checkNo}` : ""}<br />{zh ? "将同时：作废关联支票、删除关联账单与凭证归档、该付款阶段退回「计划中」；原记录快照保留在审计。" : "Voids the check, removes the bill & vouchers, releases the milestone; snapshot kept for audit."}</DialogDescription>
        </DialogHeader>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={zh ? "删除原因（必填）" : "Reason (required)"} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="admin-delete-reason" />
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" disabled={busy} onClick={submit} data-testid="admin-delete-submit" className="bg-red-600 hover:bg-red-700 text-white">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (zh ? "确认删除" : "Delete")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
